import { useState, useEffect } from 'react';
import { Outlet, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useHubTabs } from '@/lib/permisos';
import { Brain, BarChart3, Terminal, Settings2, Sparkles, BookOpen, Link2, Wand2, Wrench, Zap, Home, SlidersHorizontal } from 'lucide-react';
import api from '@/lib/api';
import ModuleRelationshipHelp from '@/components/help/ModuleRelationshipHelp';

const BASE_TABS = [
  { key: 'inicio', path: '/tenant/ai/inicio', label: 'Inicio', icon: Home, module: 'ai' },
  { key: 'dashboard', path: '/tenant/ai/dashboard', label: 'Panel', icon: BarChart3, module: 'ai' },
  { key: 'console', path: '/tenant/ai/console', label: 'Probar', icon: Terminal, module: 'ai' },
  // Sprint 53 (13/N) — "Avanzado" no decía nada de lo que en verdad hace
  // (elegir modelo por caso de uso + plan B si falla el proveedor).
  { key: 'config', path: '/tenant/ai/config', label: 'Modelos y respaldo', icon: Settings2, module: 'ai' },
  { key: 'training', path: '/tenant/ai/training', label: 'Configurar', icon: Sparkles, module: 'ai_training' },
];

const SIMPLE_TAB_KEYS = ['inicio', 'training', 'knowledge', 'console', 'voice'];
const ADVANCED_MODE_KEY = 'ai_hub_advanced_mode';

const KNOWLEDGE_TAB = { key: 'knowledge', path: '/tenant/ai/knowledge', label: 'Biblioteca', icon: BookOpen, module: 'knowledge' };
const SKILLS_TAB = { key: 'skills', path: '/tenant/ai/skills', label: 'Habilidades', icon: Wand2, module: 'ai_skills' };
const TOOLS_TAB = { key: 'tools', path: '/tenant/ai/tools', label: 'Herramientas', icon: Wrench, module: 'ai_tools' };
const ORCHESTRATOR_TAB = { key: 'orchestrator', path: '/tenant/ai/orchestrator', label: 'Orchestrator', icon: Zap, module: 'orchestrator' };
const TRIGGERS_TAB = { key: 'triggers', path: '/tenant/ai/triggers', label: 'Reglas al instante', icon: Zap, module: 'flows' };
// Sprint 53 (1/N) — renombrado de "Llamadas" a "IA Voice": esta pestaña es
// SOLO configuración (saludo, voz, despedida, cooldown), el historial de
// llamadas se movió a Conversaciones > Llamadas (menos confuso: config y
// bandeja de entrada ya no comparten pantalla).
const VOICE_TAB = { key: 'voice', path: '/tenant/ai/voice-calls', label: 'IA Voice', icon: Zap, module: 'integrations' };

export default function AIHubLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [allTabs, setAllTabs] = useState(BASE_TABS);
  const [advancedMode, setAdvancedMode] = useState(() => localStorage.getItem(ADVANCED_MODE_KEY) === 'true');

  useEffect(() => {
    api.get('/tenant/ai/limits').then(r => {
      const limits = r.data || {};
      const extra = [];
      if (limits.knowledge_enabled) {
        extra.push(KNOWLEDGE_TAB);
      }
      if (limits.ai_skills_enabled) {
        extra.push(SKILLS_TAB);
      }
      if (limits.ai_tools_enabled) {
        extra.push(TOOLS_TAB);
      }
      if (limits.orchestrator_enabled) {
        extra.push(ORCHESTRATOR_TAB);
      }
      if (limits.triggers_enabled) {
        extra.push(TRIGGERS_TAB);
      }
      if (limits.voice_enabled && limits.voice_inbound_enabled) {
        extra.push(VOICE_TAB);
      }
      setAllTabs([...BASE_TABS, ...extra]);
    }).catch(() => {});
  }, []);

  const toggleAdvancedMode = () => {
    const next = !advancedMode;
    setAdvancedMode(next);
    localStorage.setItem(ADVANCED_MODE_KEY, String(next));
  };

  const modeTabs = advancedMode ? allTabs : allTabs.filter(t => SIMPLE_TAB_KEYS.includes(t.key));
  const { tabs } = useHubTabs(modeTabs);

  if (location.pathname === '/tenant/ai' || location.pathname === '/tenant/ai/') {
    return <Navigate to="/tenant/ai/inicio" replace />;
  }

  return (
    <div className="space-y-5" data-testid="ai-hub">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
            <Brain className="w-6 h-6" /> Config IA
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Centro de configuración de inteligencia artificial</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAdvancedMode}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              advancedMode ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'text-[#64748B] border-[#E2E8F0] hover:bg-[#F8F9FA]'
            }`}
            data-testid="ai-hub-advanced-toggle"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> {advancedMode ? 'Modo avanzado' : 'Modo simple'}
          </button>
          <button
            onClick={() => navigate('/tenant/integrations?category=ai')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#64748B] border border-[#E2E8F0] rounded-lg hover:bg-[#F8F9FA] transition-colors"
            data-testid="ai-hub-integrations-cta"
          >
            <Link2 className="w-3.5 h-3.5" /> Conectar proveedor IA
          </button>
        </div>
      </div>

      <ModuleRelationshipHelp hub="ai" />

      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="ai-hub-tabs">
        {tabs.map(tab => (
          <NavLink
            key={tab.key}
            to={tab.path}
            data-testid={`ai-tab-${tab.key}`}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                isActive
                  ? 'border-[#F72585] text-[#0F172A]'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`
            }
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </div>
  );
}
